import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import AppShell from "./components/layout/AppShell";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import WorkplanGanttPage from "./pages/WorkplanGanttPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import ActivityDetailPage from "./pages/ActivityDetailPage";
import ActivityFormPage from "./pages/ActivityFormPage";
import GrantsPage from "./pages/GrantsPage";
import GrantFormPage from "./pages/GrantFormPage";
import ImplementorsPage from "./pages/ImplementorsPage";
import ImplementorFormPage from "./pages/ImplementorFormPage";
import PillarsPage from "./pages/PillarsPage";
import PillarFormPage from "./pages/PillarFormPage";
import StaffTasksPage from "./pages/StaffTasksPage";
import StaffTaskDetailPage from "./pages/StaffTaskDetailPage";
import StaffTaskFormPage from "./pages/StaffTaskFormPage";
import PMVisitorLogsPage from "./pages/PMVisitorLogsPage";
import PMVisitorLogFormPage from "./pages/PMVisitorLogFormPage";
import PMVisitorLogDetailPage from "./pages/PMVisitorLogDetailPage";
import DocumentFoldersPage from "./pages/DocumentFoldersPage";
import DocumentFolderFormPage from "./pages/DocumentFolderFormPage";
import DocumentFolderDetailPage from "./pages/DocumentFolderDetailPage";
import TravelRequestsPage from "./pages/TravelRequestsPage";
import TravelRequestDetailPage from "./pages/TravelRequestDetailPage";
import TravelRequestFormPage from "./pages/TravelRequestFormPage";
import UsersPage from "./pages/UsersPage";
import UserFormPage from "./pages/UserFormPage";
import NotFoundPage from "./pages/NotFoundPage";
import { useAuth } from "./context/AuthContext";

function PrivateRoute({ component: Component, ...rest }) {
  const { isAuthenticated } = useAuth();

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <AppShell>
            <Component {...props} />
          </AppShell>
        ) : (
          <Redirect to="/login" />
        )
      }
    />
  );
}

function RoleRestrictedRoute({ component: Component, blockedRoles = [], ...rest }) {
  const { isAuthenticated, user } = useAuth();
  const role = user?.role || "";
  const isBlocked = blockedRoles.includes(role);

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          isBlocked ? (
            <Redirect to="/" />
          ) : (
            <AppShell>
              <Component {...props} />
            </AppShell>
          )
        ) : (
          <Redirect to="/login" />
        )
      }
    />
  );
}

function App() {
  const restrictedRoles = ["staff", "supervisor"];

  return (
    <>
      <Switch>
        <Route exact path="/login" component={LoginPage} />
        <PrivateRoute exact path="/" component={DashboardPage} />
        <PrivateRoute exact path="/workplan-gantt" component={WorkplanGanttPage} />
        <PrivateRoute exact path="/activities" component={ActivitiesPage} />
        <PrivateRoute exact path="/activities/new" component={ActivityFormPage} />
        <PrivateRoute exact path="/activities/:uuid/edit" component={ActivityFormPage} />
        <PrivateRoute exact path="/activities/:uuid" component={ActivityDetailPage} />
        <RoleRestrictedRoute exact path="/users" component={UsersPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/users/new" component={UserFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/users/:uuid/edit" component={UserFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/grants" component={GrantsPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/grants/new" component={GrantFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/grants/:uuid/edit" component={GrantFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/implementors" component={ImplementorsPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/implementors/new" component={ImplementorFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/implementors/:uuid/edit" component={ImplementorFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/pillars" component={PillarsPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/pillars/new" component={PillarFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/pillars/:uuid/edit" component={PillarFormPage} blockedRoles={restrictedRoles} />
        <PrivateRoute exact path="/staff-tasks" component={StaffTasksPage} />
        <PrivateRoute exact path="/staff-tasks/new" component={StaffTaskFormPage} />
        <PrivateRoute exact path="/staff-tasks/:uuid/edit" component={StaffTaskFormPage} />
        <PrivateRoute exact path="/staff-tasks/:uuid" component={StaffTaskDetailPage} />
        <RoleRestrictedRoute exact path="/pm-visitor-logs" component={PMVisitorLogsPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/pm-visitor-logs/new" component={PMVisitorLogFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/pm-visitor-logs/:uuid/edit" component={PMVisitorLogFormPage} blockedRoles={restrictedRoles} />
        <RoleRestrictedRoute exact path="/pm-visitor-logs/:uuid" component={PMVisitorLogDetailPage} blockedRoles={restrictedRoles} />
        <PrivateRoute exact path="/documents" component={DocumentFoldersPage} />
        <PrivateRoute exact path="/documents/new" component={DocumentFolderFormPage} />
        <PrivateRoute exact path="/documents/:uuid/edit" component={DocumentFolderFormPage} />
        <PrivateRoute exact path="/documents/:uuid" component={DocumentFolderDetailPage} />
        <PrivateRoute exact path="/travel-requests" component={TravelRequestsPage} />
        <PrivateRoute exact path="/travel-requests/new" component={TravelRequestFormPage} />
        <PrivateRoute exact path="/travel-requests/:uuid/edit" component={TravelRequestFormPage} />
        <PrivateRoute exact path="/travel-requests/:uuid" component={TravelRequestDetailPage} />
        <Route component={NotFoundPage} />
      </Switch>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;
